import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Eye, 
  Trash2,
  DollarSign,
  Target,
  CheckCircle,
  XCircle,
  Filter,
  Search,
  Crown,
  Star,
  Power,
  PowerOff
} from 'lucide-react';
import { 
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  TextareaAutosize,
  Alert
} from '@mui/material';
import useFetchTableData from '../../hooks/useFetchTableData';
import adminApi from '../../api/admin';
import DataTable from '../../components/common/DataTable';
import Button from '../../components/common/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import SubscriptionPlanModal from '../../components/admin/SubscriptionPlanModal';
import { GridActionsCellItem } from '@mui/x-data-grid';
import ActivateDeactivatePlanDialog from '../../components/admin/ActivateDeactivatePlanDialog';

const AdminSubscriptionPlans = () => {
  const [filters, setFilters] = useState({
    search: "",
    role: "all",
    active: "all",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [planToUpdate, setPlanToUpdate] = useState(null);
  const [statusAction, setStatusAction] = useState('');
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate()

  // Create plan mutation
  const createPlanMutation = useMutation({
    mutationFn: (planData) => adminApi.createSubscriptionPlan(planData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      setIsModalOpen(false);
      setInitialData(null);
      toast.success('Plan created successfully!');
    },
    onError: (error) => {
      console.error('Failed to create plan:', error);
      toast.error('Failed to create plan');
    }
  });

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: ({ id, planData }) => adminApi.updateSubscriptionPlan(id, planData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      setIsModalOpen(false);
      setInitialData(null);
      toast.success('Plan updated successfully!');
    },
    onError: (error) => {
      console.error('Failed to update plan:', error);
      toast.error('Failed to update plan');
    }
  });

  // Activate/Deactivate plan mutation
  const updatePlanStatusMutation = useMutation({
    mutationFn: ({ id, action, reason }) => adminApi.updatePlanStatus(id, action, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      setStatusDialogOpen(false);
      setPlanToUpdate(null);
      setReason('');
      setStatusAction('');
      toast.success(`Plan ${statusAction === 'activate' ? 'activated' : 'deactivated'} successfully!`);
    },
    onError: (error) => {
      console.error('Failed to update plan status:', error);
      toast.error(`Failed to ${statusAction} plan`);
    }
  });

  const {
    data,
    isLoading,
    isError,
    error,
    paginationModel,
    setPaginationModel,
  } = useFetchTableData({
    queryKey: "subscription-plans",
    fetchFunc: adminApi.getSubscriptionPlans,
    params: filters,
  });

  const columns = [
    {
      field: "name",
      headerName: "Plan Name",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <div className="flex items-center space-x-3">
          {params.row.price > 50 ? (
            <Crown className="w-5 h-5 text-yellow-500" />
          ) : (
            <Star className="w-5 h-5 text-blue-500" />
          )}
          <Link
            to={`${params.row.id}`}
            className="text-blue-600 hover:underline font-semibold"
          >
            {params.value}
          </Link>
        </div>
      ),
    },
    {
      field: "price",
      headerName: "Price",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <div className="flex items-center space-x-1">
          <span className="font-bold">{params.value}</span>
          <span className="text-sm text-gray-500 ml-1">
            {params.row.currency}
          </span>
        </div>
      ),
    },
    {
      field: "target_role",
      headerName: "Target Role",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'professional' ? 'primary' : 'secondary'}
          size="small"
          icon={<Target className="w-3 h-3" />}
        />
      ),
    },
    {
  field: "features",
  headerName: "Key Features",
  flex: 2,
  minWidth: 200,
  renderCell: (params) => {
    // Parse features string into array
    const parseFeatures = (featuresString) => {
      if (!featuresString || typeof featuresString !== 'string') {
        return [];
      }
      
      return featuresString
        .split(',')
        .map(feature => feature.trim())
        .filter(feature => feature.length > 0);
    };

    const features = parseFeatures(params.value);
    const displayFeatures = features.slice(0, 3);
    
    return (
      <div className="flex flex-wrap gap-1">
        {displayFeatures.slice(0, 2).map((feature, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
          >
            {feature}
          </span>
        ))}
        {features.length > 2 && (
          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
            +{features.length - 2} more
          </span>
        )}
        {features.length === 0 && (
          <span className="px-2 py-1 bg-gray-100 text-gray-300 rounded text-xs italic">
            No features
          </span>
        )}
      </div>
    );
  },
},
    {
      field: "job_application_limit",
      headerName: "Application Limits",
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div className="text-center">
          <div className="font-semibold">
            {params.value > 0 ? params.value : 'Unlimited'}
          </div>
        </div>
      ),
    },
    {
      field: "job_posting_limit",
      headerName: "Job Limits",
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div className="text-center">
          <div className="font-semibold">
            {params.value > 0 ? params.value : 'Unlimited'}
          </div>
        </div>
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        const isActive = params.value;
        return (
          <Chip 
            label={isActive ? "Active" : "Inactive"} 
            color={isActive ? "success" : "default"}
            size="small"
            icon={isActive ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      type: 'actions',
      flex: 1,
      minWidth: 150,
      getActions: (params) => {
        const isActive = params.row.is_active;
        const actions = [
          <GridActionsCellItem
            icon={<Eye className="w-4 h-4" />}
            label="View"
            onClick={() => handleView(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<Edit className="w-4 h-4" />}
            label="Edit"
            onClick={() => handleEdit(params.row)}
            showInMenu
          />,
        ];

        // Add activate/deactivate action
        actions.push(
          <GridActionsCellItem
            icon={isActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
            label={isActive ? "Deactivate" : "Activate"}
            onClick={() => handleStatusChange(params.row, isActive ? 'deactivate' : 'activate')}
            showInMenu
            sx={{ color: isActive ? "warning.main" : "success.main" }}
          />
        );

        return actions;
      },
    },
  ];

  // Handler functions
  const handleView = (plan) => {
    console.log('View plan:', plan);
    // Navigate to plan details page
    navigate(`/admin/subscription-plans/${plan.id}`);
  };

  const handleEdit = (plan) => {
    console.log('Edit plan:', plan);
    setInitialData(plan);
    setIsModalOpen(true);
  };

  const handleStatusChange = (plan, action) => {
    setPlanToUpdate(plan);
    setStatusAction(action);
    setStatusDialogOpen(true);
  };

  const confirmStatusChange = () => {
    if (planToUpdate) {
      updatePlanStatusMutation.mutate({ 
        id: planToUpdate.id, 
        action: statusAction, 
        reason: reason 
      });
    }
  };

  const handleCreateNew = () => {
    setInitialData(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setInitialData(null);
  };

  const handleSubmitPlan = (planData) => {
    if (initialData) {
      // Update existing plan
      updatePlanMutation.mutate({ id: initialData.id, planData });
    } else {
      // Create new plan
      createPlanMutation.mutate(planData);
    }
  };

  const handleSearchChange = (event) => {
    setFilters(prev => ({
      ...prev,
      search: event.target.value
    }));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleRoleFilterChange = (event) => {
    setFilters(prev => ({
      ...prev,
      role: event.target.value
    }));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleStatusFilterChange = (event) => {
    setFilters(prev => ({
      ...prev,
      active: event.target.value
    }));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      role: "all",
      active: "all",
    });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Check if any filters are active
  const hasActiveFilters = filters.search || filters.role !== "all" || filters.active !== "all";

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading subscription plans: {error?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Subscription Plans</h1>
          <p className="text-secondary-600 mt-1">
            {data ? `Total: ${data.count} plans` : 'Manage subscription plans and pricing'}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Button 
            onClick={handleClearFilters}
            variant="outline"
            disabled={!hasActiveFilters}
          >
            Clear Filters
          </Button>
          <Button 
            onClick={handleCreateNew}
            icon={<Plus className="w-4 h-4 mr-2" />}
          >
            Create New Plan
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-secondary-500" />
              <Typography variant="subtitle1" className="text-secondary-900">
                Filters
              </Typography>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <TextField
                size="small"
                placeholder="Search plans..."
                value={filters.search}
                onChange={handleSearchChange}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search className="w-4 h-4 text-gray-400" />
                      </InputAdornment>
                    ),
                  },
                }}
                className="w-full sm:w-64"
              />
              <FormControl size="small" className="min-w-[150px]">
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={handleRoleFilterChange}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="recruiter">Recruiter</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" className="min-w-[150px]">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.active}
                  label="Status"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DataTable */}
      <DataTable
        rows={data?.results}
        columns={columns}
        loading={isLoading}
        rowCount={data?.count}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />

      {/* Empty State */}
      {!isLoading && data?.results?.length === 0 && (
        <Card className="mt-6">
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-gray-400" />
            </div>
            <Typography variant="h6" className="text-secondary-900 mb-2">
              No subscription plans found
            </Typography>
            <Typography variant="body2" className="text-secondary-600 mb-4">
              {hasActiveFilters 
                ? 'No plans match your current filters.'
                : 'There are no subscription plans in the system yet.'
              }
            </Typography>
            <div className="space-x-3">
              {hasActiveFilters && (
                <Button 
                  onClick={handleClearFilters}
                  variant="outline"
                >
                  Clear Filters
                </Button>
              )}
              <Button 
                onClick={handleCreateNew}
                icon={<Plus className="w-4 h-4 mr-2" />}
              >
                Create First Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Plan Modal */}
      <SubscriptionPlanModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPlan}
        isLoading={createPlanMutation.isLoading || updatePlanMutation.isLoading}
        title={initialData ? "Update Plan" : "Create New Subscription Plan"}
        submitButtonText={initialData ? "Update Plan" : "Create Plan"}
        initialData={initialData}
      />

      {/* Activate/Deactivate Confirmation Dialog */}
      
<ActivateDeactivatePlanDialog
  open={statusDialogOpen}
  onClose={() => setStatusDialogOpen(false)}
  onConfirm={(action, reason) => {
    if (planToUpdate) {
      updatePlanStatusMutation.mutate({ 
        id: planToUpdate.id, 
        action, 
        reason 
      });
    }
  }}
  isLoading={updatePlanStatusMutation.isLoading}
  plan={planToUpdate}
  action={statusAction}
  reason={reason}
  onReasonChange={setReason}
/>
    </div>
  );
}

export default AdminSubscriptionPlans;